<script lang="ts" setup>
import { closeModal } from '~/utils/Modal'
import RModal from '~/components/organs/Modal.vue'
import { PaymentFailCode } from '~/static/enums/payment-fail-code'
import { DESKTOP_WIDTH } from '~/static/window-sizing'

interface Props {
  title: string
  url: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'success'): void
  (e: 'fail', code: PaymentFailCode): void
}>()

const windowWidth = ref(DESKTOP_WIDTH)
const changeWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

const formLoaded = ref(false)
onMounted(() => {
  let tinkoffScript = document.createElement('script')
  tinkoffScript.setAttribute('src', 'https://integrationjs.t-static.ru/integration.js')
  document.head.appendChild(tinkoffScript)
  tinkoffScript.addEventListener('load', async () => {
    if (!('PaymentIntegration' in window)) {
      window.parent.postMessage('close')
      return
    }

    const key = useRuntimeConfig().public.tinkoffTerminalkey
    const initConfig = {
      terminalKey: typeof key === 'number' ? (key as number).toString() : key,
      product: 'eacq',
      features: {
        iframe: {},
      },
    }

    window.PaymentIntegration.init(initConfig)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(async (integration: any) => {
        const MAIN_INTEGRATION_NAME = 'main-integration-name'
        const iframeConfig = {
          router: {
            redirectCallback: async (url: string) => {
              container?.querySelector('iframe')?.setAttribute('src', url)
            },
          },
        }
        const mainPaymentIntegration = await integration.iframe.create(MAIN_INTEGRATION_NAME, iframeConfig)
        const container = document.getElementById('paymentContainer')
        await mainPaymentIntegration.mount(container, props.url)
        formLoaded.value = true
      })
      .catch()
  })
  changeWindowWidth()
  window.addEventListener('resize', changeWindowWidth)
  window.addEventListener('message', handleIframeMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', changeWindowWidth)
  window.removeEventListener('message', handleIframeMessage)
})

const handleIframeMessage = (event: MessageEvent) => {
  console.log(event)
  if (event.data === 'success') {
    emit('success')
  // } else if (event.data === 'fail') {
  //   emit('fail', PaymentFailCode.TryAgain)
  } else if (event.data === 'close') {
    closeModal()
  } else if (typeof event.data === 'string' && event.data.includes('fail')) {
    const tinkoffErrorCode = parseInt(event.data.replace('fail-', ''))
    if (isNaN(tinkoffErrorCode)) {
      return
    }
    if (tinkoffErrorCode === 1051) {
      emit('fail', PaymentFailCode.InsufficientFunds)
    } else if (
      [ 99, 1057, 1065, 1089 ].includes(tinkoffErrorCode) ||
      (tinkoffErrorCode > 1034 && tinkoffErrorCode < 1043)
    ) {
      emit('fail', PaymentFailCode.TransactionRefused)
    } else if ([ 219, 231, 244, 252, 1006, 1012, 1014, 101, 1033, 1054, 1082, 1091 ].includes(tinkoffErrorCode)) {
      emit('fail', PaymentFailCode.IncorectData)
    } else {
      emit('fail', PaymentFailCode.TryAgain)
    }
  }
}

const iframeWidth = computed(() => {
  if (windowWidth.value >= DESKTOP_WIDTH) {
    return 466
  }
  return windowWidth.value - 24
})

</script>

<template>
  <RModal name-modal="payment">
    <template #header>
      <h5 class="modal-title fs-5">
        {{ title }}
      </h5>
    </template>
    <template #body>
      <div
        v-if="!formLoaded"
        class="loading-area"
      >
        <div
          class="spinner-border"
          role="status"
        />
      </div>
      <div class="iframe-wrap">
        <div
          id="paymentContainer"
          :style="formLoaded ? {
            width: iframeWidth + 'px',
          } : {}"
        />
      </div>
    </template>
  </RModal>
</template>

<style lang="scss" scoped>
.spinner-border {
  color: $indigo-500;
}

.iframe-wrap {
   @include media-breakpoint-down(lg) {
    display: flex;
    justify-content: center;
    max-height: calc(100vh - 108px);
    overflow-y: auto;
  }

  @include media-breakpoint-down(md) {
    max-height: calc(100vh - 92px);
  }
}

.loading-area {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 490px;
  background: $indigo-100-light;
  margin-bottom: rem(16px);
}

:deep(.modal-body) {
  background: #f6f7f8;
  @include media-breakpoint-down(md) {
    padding-bottom: rem(24px);
  }
}

:deep(.modal-content) {
  @include media-breakpoint-down(md) {
    padding-bottom: 0;
  }  
}
</style>
